/* One motion controller per product section. Reuses SuvornaMotion's GSAP,
   ScrollTrigger and Lenis runtime; never owns/intercepts wheel or touch input. */
(function () {
  'use strict';
  window.SuvornaProductMotion = {
    create(root) {
      const gsap = window.gsap, ST = window.ScrollTrigger;
      // Keep the same full-image frame when WebGL or motion is unavailable.
      root.querySelectorAll('[data-pdp-opening-surface]').forEach(art => window.SuvornaProductScene?.prepare(art));
      // Semantic content adaptation also applies under reduced motion.
      const cards = [];
      root.querySelectorAll('[data-pdp-story] .grid__item').forEach(card => {
        const children = [...card.children].filter(child => child.classList.contains('grid__item'));
        const photo = children.find(child => child.querySelector('img') && !child.textContent.trim());
        const copy = children.find(child => !child.querySelector('img') && child.textContent.trim());
        if (!photo || !copy || children.length !== 2) return;
        card.classList.add('sv-pdp__highlight-card');
        card.classList.toggle('is-reversed', cards.length % 2 === 1);
        photo.classList.add('sv-pdp__feature-image'); copy.classList.add('sv-pdp__feature-copy');
        copy.dataset.pdpFeatureIndex = String(cards.length+1).padStart(2,'0') + ' / A CLOSER LOOK';
        cards.push(card);
      });
      if (!gsap || !ST) return { dispose() {}, setOpeningImage() { return false; } };
      gsap.registerPlugin(ST);
      const mm = gsap.matchMedia();
      const listeners = new AbortController();
      let disposed = false, refreshTimer, mediaTween, activeScene, preserveRefreshScroll=false;
      const refresh = event => {
        if(event?.type==='toggle'||event?.type==='pdp:details-settled'||event?.type==='pdp:share-settled')preserveRefreshScroll=true;
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => {
          if(disposed)return;
          if(root.querySelector('[data-pdp-animating]')){refresh();return;}
          const measure=()=>{window.SuvornaMotion?.lenis?.resize();ST.refresh();};
          const preserve=preserveRefreshScroll;preserveRefreshScroll=false;
          if(preserve&&window.SuvornaScrollAnchor)window.SuvornaScrollAnchor.preservePosition(measure);
          else measure();
        }, 120);
      };
      // Decode-driven layout changes are batched, never refreshed from a render loop.
      root.querySelectorAll('img').forEach(image => { if (!image.complete) image.addEventListener('load',refresh,{once:true,signal:listeners.signal}); });
      root.querySelector('details')?.addEventListener('toggle',event=>{if(!event.target.dataset.pdpAnimating)refresh(event);},{signal:listeners.signal});
      root.addEventListener('pdp:details-settled',refresh,{signal:listeners.signal});
      root.addEventListener('pdp:share-settled',refresh,{signal:listeners.signal});
      document.fonts.ready.then(()=>{if(!disposed) refresh();});
      mm.add({ motion: '(prefers-reduced-motion: no-preference)', wide: '(min-width: 64rem) and (min-height: 48rem)' }, context => {
        if (!context.conditions.motion) return;
        root.classList.add('is-motion');
        const opening = root.querySelector('[data-pdp-opening]'), stage = root.querySelector('[data-pdp-opening-stage]');
        const art = root.querySelector('[data-pdp-opening-art]');
        let scene = null, observer = null, visible = false;

        const titleMasks = [];
        // Preserve inline emphasis and accessible text; wrap text nodes only.
        const words = element => {
          if (!element) return [];
          if (element.dataset.pdpWords) return [...element.querySelectorAll('.sv-pdp__word')];
          element.dataset.pdpWords = 'true'; titleMasks.push(element);
          const walker = document.createTreeWalker(element,NodeFilter.SHOW_TEXT), nodes = [];
          while (walker.nextNode()) nodes.push(walker.currentNode);
          nodes.forEach(node => {
            const fragment = document.createDocumentFragment();
            node.textContent.split(/(\s+)/).forEach(part => {
              if (!part.trim()) { fragment.append(document.createTextNode(part)); return; }
              const mask = document.createElement('span'), word = document.createElement('span');
              mask.className = 'sv-pdp__word-mask'; word.className = 'sv-pdp__word'; word.textContent = part;
              mask.append(word); fragment.append(mask);
            });
            node.replaceWith(fragment);
          });
          return [...element.querySelectorAll('.sv-pdp__word')];
        };
        if (opening) {
          scene = window.SuvornaProductScene?.create(root.querySelector('[data-pdp-opening-surface]'));
          activeScene = scene;
          gsap.from(words(root.querySelector('[data-pdp-opening-title]')), { yPercent: 110, duration: .9, stagger: .08, ease: 'power3.out' });
          gsap.from(art, { opacity: 0, y: '1.5rem', duration: .8, ease: 'power3.out' });
          observer = new IntersectionObserver(entries=>{visible = entries[0].isIntersecting;}, {rootMargin:'10%'});
          observer.observe(stage);
        }
        const tick = () => { if (visible && !document.hidden) scene?.render(); };
        if (scene) gsap.ticker.add(tick);
        const reveal = (target, trigger, options = {}) => {
          if (!target || (Array.isArray(target) && !target.length)) return;
          return gsap.from(target, { y: '3rem', opacity: 0, duration: .8, stagger: .07, ease: 'power3.out', ...options,
            scrollTrigger: { trigger: trigger || target, start: 'top 88%', toggleActions: 'play none none reverse' } });
        };
        const purchase = root.querySelector('[data-pdp-purchase]');
        const body=root.querySelector('.sv-pdp__body'), handover=root.querySelector('.sv-pdp__handover');
        let blinds=null;
        if(stage){
          blinds=document.createElement('div');blinds.className='sv-pdp__blinds';blinds.setAttribute('aria-hidden','true');
          const bands=Array.from({length:24},()=>{const band=document.createElement('i');blinds.append(band);return band;});stage.append(blinds);
          const clamp=gsap.utils.clamp(0,1);
          const breadcrumb=root.querySelector('.sv-pdp__breadcrumb'),info=root.querySelector('.sv-pdp__information');
          // Same 24-band fold and phase constants as vision and the menu footer.
          // Lenis already smooths scroll: paint the fold directly, without a
          // second time-based scrub that would let bands finish after release.
          const paint=p=>{
            const q=clamp((p-.15)/.85),close=clamp(q/.55);
            bands.forEach((band,i)=>{const start=((23-i)/23)*.7;band.style.transform='scaleY('+clamp((close-start)/.3).toFixed(4)+')';});
            const seam=clamp((p-.64)/.2),copy=clamp((p-.72)/.18);
            handover.style.setProperty('--pdp-rule',seam);handover.style.setProperty('--pdp-turn',(-180*(1-seam))+'deg');
            handover.style.opacity=String(seam>0?1:0);
            gsap.set([breadcrumb,info],{opacity:copy,y:(1-copy)*3+'rem'});
          };
          ST.create({id:'pdp-hero-handover',trigger:opening,start:'top top',end:()=>'+='+innerHeight*1.4,pin:stage,pinSpacing:false,invalidateOnRefresh:true,onUpdate:self=>paint(self.progress),onRefresh:self=>paint(self.progress)});
        }
        const storyRoot=root.querySelector('[data-pdp-story]');
        const companions=root.querySelector('.sv-pdp__companions');
        const chapters=window.SuvornaProductChapters?.create(root,{gsap,ST,cards,words,wide:context.conditions.wide});
        // The implements fold (suvorna-product-chapters.js) is built first, so the
        // description tail gathered below is whatever follows its stack.
        // Without a chapter (Ahtisam, 12 September 2026: every product hands over the same
        // way) the loose tail of the story after its last structured block, paragraphs, legacy
        // divs or un-chaptered highlight cards, is gathered into the same description block so
        // it reads and holds like the chapter's. A tail too thin to fill a screen stays in flow.
        const meaningful=el=>!el.hidden&&(el.textContent.trim().length>0||!!el.querySelector('img,video,iframe'));
        // The description block itself is taken at every width, so its copy is centred and held on phones too.
        let description=root.querySelector('.sv-pdp__chapter-after')||storyRoot?.querySelector(':scope > .sv-pdp__description'),gathered=false;
        if(!description&&storyRoot&&context.conditions.wide){
          const kids=[...storyRoot.children];
          let last=-1;kids.forEach((kid,i)=>{if(kid.matches('.sv-pdp__reading,.sv-pdp__chapter-entry,.pin-spacer,.sv-pdp__chapter'))last=i;});
          const tail=kids.slice(last+1),body=tail.filter(meaningful);
          if(body.length&&(body.reduce((n,kid)=>n+kid.textContent.trim().length,0)>=160||body.some(kid=>kid.querySelector('img')))){
            description=document.createElement('div');description.className='sv-pdp__chapter-after';gathered=true;
            tail[0].before(description);tail.forEach(kid=>description.append(kid));
          }
        }
        let descriptionContent=null;
        if(description){
          description.dataset.pdpReading='description';
          descriptionContent=document.createElement('div');
          descriptionContent.className='sv-pdp__reading-content sv-pdp__description-content';
          while(description.firstChild)descriptionContent.append(description.firstChild);
          description.append(descriptionContent);
          // No loop behind the description any more (Ahtisam, 10 October 2026, as on the website).
        }
        // A short, smooth plateau in presentation space, like the collection
        // orbit. Native scroll keeps moving; no wheel interception or snapping.
        // What the companions plate rides over: the description block, or failing that the last
        // reading stage.
        let held=descriptionContent,heldReading=null;
        if(!held&&storyRoot&&companions){const lastKid=[...storyRoot.children].filter(meaningful).pop();if(lastKid&&lastKid.matches('.sv-pdp__reading')){heldReading=lastKid;held=lastKid.querySelector('.sv-pdp__reading-content');}}
        root.querySelectorAll('[data-pdp-reading]').forEach(reading => {
          if((reading===description||reading===heldReading)&&companions)return; // its foot is held by pdp-companions-rise instead
          const content=reading.querySelector('.sv-pdp__reading-content');
          const isFeatures=reading.dataset.pdpReading==='features',isPurchase=reading.dataset.pdpReading==='purchase';
          // The questions and the companions hold like the tall readings (as on the website):
          // a gentle scroll rests them in view, a fast one passes straight through.
          const isFaq=reading.dataset.pdpReading==='faq',isCompanions=reading.dataset.pdpReading==='companions';
          // Native sticky uses layout coordinates, while the soft stop moves
          // the content visually. Share the offset so sticky stays in sync.
          const holdProperty=isFeatures?'--pdp-hold-y':'y';
          if(isFeatures)gsap.set(content,{'--pdp-hold-y':'0px'});
          const move=gsap.quickTo(content,holdProperty,{duration:.16,ease:'power2.out'});
          const reposition=gsap.quickTo(content,holdProperty,{duration:.42,ease:'power2.inOut'});
          const setOffset=(value,refreshing)=>{
            const current=parseFloat(gsap.getProperty(content,holdProperty))||0;
            if(isPurchase&&refreshing){move.tween.pause();reposition(value,current);}
            else {reposition.tween.pause();move(value,current);}
          };
          let previous=null,shown=0,direction=0,bypass=false;
          const heldOffset=distance=>{
            if(distance<=.18)return 0;
            const t=gsap.utils.clamp(0,1,(distance-.18)/.32);
            return .5*t*t*t*(7.44+t*(-10.52+t*4.08));
          };
          const isDescription=reading===description;
          const tallReading=()=>(isFeatures||isPurchase||isFaq||isCompanions)&&content.offsetHeight>innerHeight-16*parseFloat(getComputedStyle(document.documentElement).fontSize);
          const readingStart=()=>{
            if(isDescription)return 'top top';
            if(tallReading()){const inset=12*parseFloat(getComputedStyle(document.documentElement).fontSize);return 'top '+(inset+innerHeight*.25-parseFloat(getComputedStyle(reading).paddingTop))+'px';}
            return 'center 80%';
          };
          const updateHold=(self,refreshing=false)=>{
              if(isPurchase&&ST.isRefreshing&&!refreshing)return;
              const raw=self.progress,movement=previous===null?0:Math.sign(raw-previous);
              if(isPurchase&&!refreshing&&!movement&&previous!==null&&reposition.tween.isActive())return;
              const room=innerHeight-16*parseFloat(getComputedStyle(document.documentElement).fontSize);
              const fast=!refreshing&&Math.abs(self.getVelocity())>Math.max(700,innerHeight*1.1);
              if(!self.isActive){previous=null;bypass=false;setOffset(0,refreshing);return;}
              if(previous===null||(movement&&direction&&movement!==direction))bypass=fast;
              else if(fast)bypass=true;
              const offset=raw-.5;
              let next=bypass||(!isFeatures&&!isPurchase&&content.offsetHeight>room)?raw:isDescription?2*heldOffset(raw*.5):.5+Math.sign(offset)*heldOffset(Math.abs(offset));
              if(previous!==null){if(movement>0)next=Math.max(shown,next);else if(movement<0)next=Math.min(shown,next);else next=shown;}
              setOffset((raw-next)*(self.end-self.start),refreshing);previous=raw;shown=next;if(movement)direction=movement;
            };
          ST.create({id:'pdp-reading-'+reading.dataset.pdpReading,trigger:reading,start:readingStart,end:()=>isDescription||tallReading()?'+='+innerHeight*.5:'center 30%',
            onUpdate:self=>updateHold(self),
            onRefresh:self=>{
              previous=null;bypass=false;
              // Re-measuring an accordion must not discard the visible soft
              // stop offset. Recompute it and use the existing smooth setter.
              if(isPurchase)updateHold(self,true);
              else gsap.set(content,{[holdProperty]:0});
            }
          });
        });
        // The companions ride up over the description (Ahtisam, 12 September 2026, after the
        // velar reference): once the description's foot meets the screen's, its content is held
        // while the white companions plate, hairline first, scrolls over it. No pin spacing: the
        // plate arrives through the same scroll that would have carried the description away,
        // and the hold ends when the plate's top reaches the top of the screen.
        // Before they come, the description holds still (as on the website, from version 2):
        // --pdp-desc-rest lengthens its black ground below the copy by DESC_REST of a screen
        // (suvorna-product-v1.css), and the copy is held from the moment it has fully arrived:
        // centred, for a short one, or its foot at the screen's.
        const DESC_REST=.7,descRest=()=>Math.round(innerHeight*DESC_REST);
        const setRest=()=>description?.style.setProperty('--pdp-desc-rest',descRest()+'px');
        if(held&&companions){
          if(!heldReading){setRest();ST.addEventListener('refreshInit',setRest);}
          ST.create({id:'pdp-companions-rise',trigger:heldReading||description,start:()=>heldReading?'bottom bottom':'bottom-='+descRest()+' bottom',endTrigger:companions,end:'top top',pin:held,pinSpacing:false,invalidateOnRefresh:true});
        }
        root.querySelectorAll('.sv-pdp__reading-divider').forEach(divider=>{
          gsap.fromTo(divider,{'--pdp-rule':0,'--pdp-turn':'-180deg'},{'--pdp-rule':1,'--pdp-turn':'0deg',ease:'none',scrollTrigger:{trigger:divider,start:'top 90%',end:'top 55%',scrub:.4}});
        });
        root.querySelectorAll('.sv-pdp__section-heading').forEach(heading=>{
          gsap.fromTo(heading,{'--pdp-rule':0,'--pdp-turn':'-180deg'},{'--pdp-rule':1,'--pdp-turn':'0deg',ease:'none',scrollTrigger:{trigger:heading,start:'top 95%',end:'top 60%',scrub:.4}});
          reveal(words(heading.querySelector('h2')),heading,{y:0,yPercent:110,opacity:1,duration:.9});
        });
        const overview = root.querySelector('[data-pdp-story] #overview');
        if (overview) {
          reveal(overview.querySelector('h2'),overview,{y:'4rem'});
          reveal(overview.querySelectorAll('p').length ? [...overview.querySelectorAll('p')] : null,overview,{delay:.12});
          const photo = overview.querySelector('img');
          if(photo) gsap.fromTo(photo,{yPercent:10,rotation:3,scale:.93},{yPercent:-7,rotation:-2,scale:1.03,ease:'none',scrollTrigger:{trigger:overview,start:'top bottom',end:'bottom top',scrub:.65}});
        }
        root.querySelectorAll('#feature-list li, #tech-specs li').forEach(item=>reveal(item,item,{y:'2rem',duration:.6}));
        cards.filter(card=>!chapters?.used.has(card)).forEach(card=>{
          const photo=card.querySelector('.sv-pdp__feature-image'),copy=card.querySelector('.sv-pdp__feature-copy');
          gsap.fromTo(card,{'--pdp-rule':0,'--pdp-turn':'-180deg'},{'--pdp-rule':1,'--pdp-turn':'0deg',ease:'none',scrollTrigger:{trigger:card,start:'top 95%',end:'top 60%',scrub:.4}});
          gsap.fromTo(photo,{yPercent:14,rotation:card.classList.contains('is-reversed')?5:-5,rotationX:12,scale:.9},{yPercent:-7,rotation:0,rotationX:-5,scale:1,ease:'none',scrollTrigger:{trigger:card,start:'top bottom',end:'bottom top',scrub:.7}});
          reveal(copy,copy,{y:'4rem',duration:.9});
        });
        root.querySelectorAll('.sv-pdp__description-content > p, .sv-pdp__description-content > h2, .sv-pdp__description-content > h3, .sv-pdp__description-content > ul, .sv-pdp__story > p, .sv-pdp__story > ul, .sv-pdp__story > h2, .sv-pdp__story > h3, .sv-pdp__story > strong, .sv-pdp__image-pair > *').forEach(item=>reveal(item,item));
        reveal([...root.querySelectorAll('.sv-pdp__related-item')],root.querySelector('.sv-pdp__related-grid'),{y:'4rem',stagger:.1});
        refresh();
        return () => {
          blinds?.remove(); handover.style.removeProperty('--pdp-rule');handover.style.removeProperty('--pdp-turn');handover.style.removeProperty('opacity');
          ST.removeEventListener('refreshInit',setRest);description?.style.removeProperty('--pdp-desc-rest');
          if(descriptionContent){while(descriptionContent.firstChild)descriptionContent.before(descriptionContent.firstChild);descriptionContent.remove();delete description.dataset.pdpReading;}
          if(gathered){while(description.firstChild)description.before(description.firstChild);description.remove();}
          chapters?.dispose(); observer?.disconnect(); gsap.ticker.remove(tick); scene?.dispose(); activeScene = null; root.classList.remove('is-motion');
          titleMasks.forEach(element=>{
            element.querySelectorAll('.sv-pdp__word-mask').forEach(mask=>mask.replaceWith(document.createTextNode(mask.textContent)));
            delete element.dataset.pdpWords; element.normalize();
          });
        };
      },root);
      // The dock (Ahtisam, 10 October 2026: the website's, from its 30 September version). Buy now
      // moves into it, so the trolley roll and the sold-out state work as they do in place; a holder
      // keeps the row's room so nothing below moves. Only while the chosen variant is in stock.
      // The row scrolls with the page untouched until it comes within `travel` of the dock's line.
      // Over that last stretch the button's body (its skin) squeezes towards the dock's own size and
      // place, 1:1 with the scroll, while the trolley and the words keep their size; on arrival the
      // dock takes the row, parked at the top right at a quiet 40%. Scrolling back runs the same in
      // reverse. It stands down, fading, when the hairline under the questions (or, without them,
      // the companions) reaches the line.
      // Progress is read from the row's place on screen each frame rather than from a scrubbed
      // trigger: the purchase section's soft hold moves the row off the scroll's pace on this page.
      const dock=root.querySelector('[data-pdp-dock]'),row=root.querySelector('[data-pdp-purchase] .sv-pdp__purchase');
      const box=row?.querySelector('.product-form__submit');
      let dockAllowed=root.querySelector('[data-pdp-availability]')?.dataset.available==='true',holder=null,home=null,dockBtn=null,skin=null,inner=null,fading=false;
      const still=()=>matchMedia('(prefers-reduced-motion: reduce)').matches;
      const phone=()=>matchMedia('(max-width: 47.99rem)').matches;
      const rem=()=>parseFloat(getComputedStyle(document.documentElement).fontSize);
      // Where the website's dock rests, in this theme's rem (the website's root is 16px): its line,
      // the button's top, 14.72rem down (12.8rem on phones), flush with the page's right content
      // edge, 5.6rem in (1.92rem on phones). REST: the plate's padding and the button's width.
      const REST={pad:.6,button:6.8};
      const restLine=()=>phone()?12.8:14.72,restRight=()=>phone()?1.92:5.6;
      const travel=()=>Math.min(innerHeight*.18,160);
      const related=root.querySelector('[data-pdp-reading="companions"]'),faqDivider=root.querySelector('.sv-pdp__faq-divider');
      // The squeeze deforms the body, not the words: the button is split once into a skin (its
      // paint, which yields) and an inner (the trolley and the label, which stay themselves).
      const dressButton=()=>{
        if(!box||box.querySelector(':scope > .sv-pdp__squeeze-skin'))return;
        skin=document.createElement('i');skin.className='sv-pdp__squeeze-skin';skin.setAttribute('aria-hidden','true');
        inner=document.createElement('span');inner.className='sv-pdp__squeeze-inner';
        const kids=[...box.children];
        const cart=kids.find(n=>n.classList&&n.classList.contains('sv-pdp__cart'));
        const label=kids.find(n=>n.tagName==='SPAN');
        box.prepend(skin);inner.append(...[cart,label].filter(Boolean));box.append(inner);
      };
      const undress=()=>{
        if(!box.style.transition&&!skin?.style.transform)return;
        if(skin)skin.style.transform='';
        if(inner){inner.style.transform='';inner.style.transition='';}
        box.style.transition='';box.style.background='';box.style.borderColor='';
      };
      const undock=()=>{undress();if(holder&&dock.contains(row)){holder.append(row);holder.style.height='';dock.hidden=true;}};
      // The row's sizes, read in place.
      const measure=()=>{
        const rowRect=row.getBoundingClientRect(),b=box.getBoundingClientRect();
        home={dy:b.top-rowRect.top,height:rowRect.height,bright:b.right,bw:b.width,bh:b.height};
      };
      const dockX=width=>document.documentElement.clientWidth-restRight()*rem()-width+REST.pad*rem();
      // The dock's own cut, read once: the row is parked in the dock at rest, the button's box
      // noted, and the row goes straight back to the flow.
      const measureDock=()=>{
        measure();
        holder.style.height=home.height+'px';
        dock.style.setProperty('--d','1');dock.hidden=false;dock.append(row);
        dock.style.transform='translate3d('+dockX(dock.offsetWidth).toFixed(2)+'px,'+((restLine()-REST.pad)*rem()).toFixed(2)+'px,0)';
        const b=box.getBoundingClientRect();dockBtn={right:b.right,w:b.width,h:b.height};
        dock.hidden=true;holder.append(row);holder.style.height='';
        measure();
      };
      const standDown=()=>{
        if(!holder?.isConnected)return;
        if(still()||dock.hidden){undock();return;}
        if(fading)return;
        fading=true;dock.classList.add('is-fading');
        setTimeout(()=>{fading=false;dock.classList.remove('is-fading');undock();},240);
      };
      const frame=()=>{
        if(!dockAllowed){standDown();return;}
        const r=rem(),line=restLine()*r;
        // Without that hairline or companions, it stands down as the page's end comes up the screen.
        const boundary=faqDivider||related;
        const past=boundary?boundary.getBoundingClientRect().top<line:root.getBoundingClientRect().bottom<innerHeight;
        if(past){standDown();return;}
        if(fading)return;
        const docked=dock.contains(row),top=holder.getBoundingClientRect().top;
        const progress=still()?(top+home.dy<=line?1:0):gsap.utils.clamp(0,1,(line+travel()-top)/travel());
        if(progress>=1){
          if(!docked){measure();holder.style.height=home.height+'px';dock.append(row);dock.hidden=false;undress();}
          dock.style.setProperty('--d','1');
          dock.style.transform='translate3d('+dockX(dock.offsetWidth).toFixed(2)+'px,'+(line-REST.pad*r).toFixed(2)+'px,0)';
          return;
        }
        // Below the catch the squeeze lives on the skin, in the flow: anchored at its right edge,
        // squashed from the left and pushed toward the dock's box. The inner rides the pill's centre.
        if(docked)undock();
        if(progress<=0){undress();return;}
        if(dockBtn&&home.bw){
          const tx=(dockBtn.right-home.bright)*progress,sx=1+(dockBtn.w/home.bw-1)*progress,sy=1+(dockBtn.h/home.bh-1)*progress;
          // The button's paint moves onto the skin; its own transition would soften the scroll.
          box.style.transition='none';box.style.background='transparent';box.style.borderColor='transparent';
          skin.style.transition='none';skin.style.transformOrigin='100% 50%';
          skin.style.transform='scale('+sx.toFixed(4)+','+sy.toFixed(4)+')';
          inner.style.transition='none';
          inner.style.transform='translate3d('+(tx+home.bw*(1-sx)/2).toFixed(2)+'px,0,0)';
        }
      };
      if(dock&&row&&box){
        holder=document.createElement('div');holder.className='sv-pdp__dock-holder';
        row.before(holder);holder.append(row);
        dressButton();
        measureDock();
        // After Lenis and the holds' tweens on the same ticker, so it reads this frame's page.
        gsap.ticker.add(frame);
        // New sizes after a resize: put the row back, read the cut again.
        addEventListener('resize',()=>{undock();measureDock();},{signal:listeners.signal});
      }
      return {
        setDockAllowed(available) { dockAllowed=!!available; },
        setOpeningImage(image, options) {
          return activeScene?.setSource(image ? image.currentSrc || image.src : null, options) || false;
        },
        revealMedia(target) {
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
          mediaTween?.revert();
          mediaTween=gsap.fromTo(target,{opacity:0,y:'1rem'},{opacity:1,y:0,duration:.25,ease:'power2.out',clearProps:'transform,opacity'});
        },
        dispose() { gsap.ticker.remove(frame); if(holder)undock(); disposed=true; clearTimeout(refreshTimer); listeners.abort(); mediaTween?.revert(); mm.revert(); }
      };
    }
  };
})();
